import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Truck, Storefront, MapPin, ClockClockwise } from "@phosphor-icons/react";

/**
 * TrackDeliveryPage — the customer-facing "where's my food?" screen.
 *
 * Polls ``GET /delivery/track/:id`` every 15 s and renders the store,
 * destination, and the driver's live GPS position on a Leaflet map.
 * Also shows a compact status ribbon (order placed → out for delivery
 * → delivered) with the ETA.
 */

// Leaflet ships with icons pinned to a CDN path that breaks under CRA's
// bundler. Rebind them to public marker PNGs shipped inline as data URIs
// so no external assets are needed at runtime.
const IconStore = new L.DivIcon({
  className: "",
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  html: `<div style="width:36px;height:36px;border-radius:50%;background:#2563eb;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.25);color:#fff;font-weight:bold">S</div>`,
});
const IconHouse = new L.DivIcon({
  className: "",
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  html: `<div style="width:36px;height:36px;border-radius:50%;background:#059669;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.25);color:#fff;font-weight:bold">H</div>`,
});
const IconDriver = new L.DivIcon({
  className: "",
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  html: `<div style="width:40px;height:40px;border-radius:50%;background:#f97316;display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,.3);border:3px solid #fff"><svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M3 6v10h1a3 3 0 006 0h4a3 3 0 006 0h1V6H3zm4 12a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm10 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/></svg></div>`,
});

const AutoFit = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [40, 40] });
  }, [bounds, map]);
  return null;
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};
const minsUntil = (iso) => {
  if (!iso) return null;
  return Math.max(0, Math.round((new Date(iso) - new Date()) / 60000));
};

const TrackDeliveryPage = () => {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [redirectCancelled, setRedirectCancelled] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/delivery/track/${orderId}`);
        if (alive) { setData(res.data); setError(null); }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Couldn't load your delivery status");
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [orderId]);

  // Universal tracking: if the order was auto-dispatched to a third-party
  // carrier (DoorDash / Uber / Grubhub), forward the customer to that
  // carrier's live map after a 2-second grace window so they can cancel
  // and stay on our page instead.
  useEffect(() => {
    if (!data?.third_party_tracking_url || redirectCancelled) return;
    const timer = setTimeout(() => {
      window.location.href = data.third_party_tracking_url;
    }, 2000);
    return () => clearTimeout(timer);
  }, [data?.third_party_tracking_url, redirectCancelled]);

  if (error) {
    return <div className="p-8 text-center text-sm text-muted-foreground">{error}</div>;
  }
  if (!data) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading your delivery…</div>;
  }

  const stage = data.driver_delivered_at
    ? "delivered"
    : data.driver_started_at
      ? "on_the_way"
      : data.driver_name
        ? "assigned"
        : "prepping";
  const eta = minsUntil(data.estimated_delivery_at);

  // Fit-bounds — include every point we know so the map frames the whole run.
  const pts = [];
  if (data.store_lat != null) pts.push([data.store_lat, data.store_lng]);
  if (data.delivery_lat != null) pts.push([data.delivery_lat, data.delivery_lng]);
  if (data.driver_lat != null) pts.push([data.driver_lat, data.driver_lng]);
  const bounds = pts.length >= 2 ? pts : null;
  const mapCenter = pts[0] || [39, -95];

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="track-page">
      {data.third_party_tracking_url && !redirectCancelled && (
        <div className="bg-primary text-primary-foreground text-sm px-4 py-3 flex flex-wrap items-center justify-center gap-3" data-testid="third-party-redirect-banner">
          <span>
            Your order is being delivered by <b>{(data.third_party_provider || "our partner").replace(/^\w/, c => c.toUpperCase())}</b> — opening their live map…
          </span>
          <button
            onClick={() => setRedirectCancelled(true)}
            className="underline text-xs opacity-90 hover:opacity-100"
            data-testid="cancel-redirect-btn"
          >
            Stay on this page
          </button>
        </div>
      )}
      {data.third_party_tracking_url && redirectCancelled && (
        <div className="bg-muted text-xs px-4 py-2 flex flex-wrap items-center justify-center gap-2" data-testid="third-party-manual-link">
          <span>Delivered by {(data.third_party_provider || "partner").replace(/^\w/, c => c.toUpperCase())}.</span>
          <a
            href={data.third_party_tracking_url}
            className="underline font-medium"
            target="_blank"
            rel="noreferrer"
          >
            Open live map ↗
          </a>
        </div>
      )}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto p-4">
          <div className="text-xs text-muted-foreground">{data.company_name}</div>
          <div className="font-heading text-2xl">
            {stage === "delivered" && "Delivered!"}
            {stage === "on_the_way" && `Out for delivery${eta != null ? ` — ${eta} min` : ""}`}
            {stage === "assigned" && `${data.driver_name} is on it`}
            {stage === "prepping" && "We're cooking"}
          </div>
        </div>
        <StageRibbon stage={stage} />
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-3">
        <div className="rounded-lg overflow-hidden border" style={{ height: "50vh", minHeight: 320 }} data-testid="track-map">
          <MapContainer center={mapCenter} zoom={13} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {data.store_lat != null && (
              <Marker position={[data.store_lat, data.store_lng]} icon={IconStore}>
                <Popup>{data.company_name || "Store"}</Popup>
              </Marker>
            )}
            {data.delivery_lat != null && (
              <Marker position={[data.delivery_lat, data.delivery_lng]} icon={IconHouse}>
                <Popup>{data.delivery_address}</Popup>
              </Marker>
            )}
            {data.driver_lat != null && (
              <Marker position={[data.driver_lat, data.driver_lng]} icon={IconDriver}>
                <Popup>{data.driver_name || "Driver"}</Popup>
              </Marker>
            )}
            {bounds && <AutoFit bounds={bounds} />}
          </MapContainer>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Details</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <Row icon={<Storefront size={16} />} label="From" value={data.company_name || "Store"} />
            <Row icon={<MapPin size={16} />} label="To" value={data.delivery_address} />
            {data.driver_name && (
              <Row icon={<Truck size={16} />} label="Driver" value={data.driver_name} />
            )}
            {eta != null && stage !== "delivered" && (
              <Row icon={<ClockClockwise size={16} />} label="ETA" value={`~${eta} min · ${fmtTime(data.estimated_delivery_at)}`} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Row = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="text-primary shrink-0 mt-0.5">{icon}</div>
    <div className="flex-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div>{value}</div>
    </div>
  </div>
);

const StageRibbon = ({ stage }) => {
  const stages = [
    { key: "prepping", label: "Prepping" },
    { key: "assigned", label: "Assigned" },
    { key: "on_the_way", label: "On the way" },
    { key: "delivered", label: "Delivered" },
  ];
  const activeIdx = stages.findIndex((s) => s.key === stage);
  return (
    <div className="max-w-3xl mx-auto flex px-4 pb-3 gap-1">
      {stages.map((s, i) => (
        <div
          key={s.key}
          className={`flex-1 h-1.5 rounded-full ${i <= activeIdx ? "bg-primary" : "bg-secondary"}`}
          data-testid={`stage-${s.key}${i <= activeIdx ? "-active" : ""}`}
          title={s.label}
        />
      ))}
    </div>
  );
};

export default TrackDeliveryPage;
